import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, Checkbox, TextField } from "@mui/material"
import { ContactUsSubjects, postWebContactUsSendContactUs } from "api"
import { useLocale, useTranslations } from "next-intl"
import CustomHelperText from "pages/Contact/CustomHelperText"
import { LegacyRef, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, useForm } from "react-hook-form"
import { useInView } from "react-intersection-observer"
import colors from "theme/colors"
import {
  CheckboxIcon,
  CheckedCheckboxIcon,
  FormSubmitFailIcon,
  FormSubmitSuccessIcon,
  NewText,
  TransitionUp,
  transitionDelayTime,
  useInViewConfig,
  useTransitionUpStyles,
} from "ui"
import { sharedColors } from "ui/theme/colors"
import { delayTime, styleObjectToCss } from "ui/transitions/utils"
import { extractProperty, submitClick, submitError, submitSuccess } from "utils"
import * as yup from "yup"

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledDiv = styled.div`
  max-width: 500px;
  padding-top: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 96px;
    padding-bottom: 112px;
  }
`

const StyledFormDiv = styled.div`
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const FormContainer = styled.form`
  margin-top: 16px;
`

const InputLengthContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
`

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  padding: 1px 10px 0px 0;
  ${(props) =>
    !props.status
      ? `  rect {
    stroke: red;
  }`
      : ""};
`

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }
`

const CheckBoxesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`

const StyledButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: 40px;
`

const StyledButton = styled(Button)`
  min-width: 153px;
  border: 2px solid ${sharedColors.black};
  border-radius: 200px;
  text-decoration: none;
  color: #fff;
  text-transform: none;
  width: 100%;
  background: ${sharedColors.black};
  :hover {
    background: rgba(24, 136, 226, 1);
    border: 2px solid rgba(24, 136, 226, 1);
    text-decoration: none;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 16px;
    width: auto;
  }
`
const StyledBigContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: start;
  gap: 8px;
`

const StyledTextContainer = styled.div`
  display: flex;
  text-align: start;
  gap: 16px;
`

type Props = {
  data: any
  companyId?: number
}

const messagesInvalid = {
  en: {
    required: "This field cannot be left blank ",
    max: "Max characters limit",
    incorrect: "Details are incorrect",
  },
  he: {
    required: "required",
    max: "Max characters limit",
    incorrect: "Details are incorrect",
  },
}
const generateSchema = (locale: string) => {
  const msg = messagesInvalid[locale as "en" | "he"]

  return yup.object({
    senderName: yup.string().required(msg.required).min(5, msg.incorrect).max(50, msg.incorrect),

    senderEmail: yup.string().email(msg.incorrect).required(msg.required),
    contactUsSubject: yup.string().required(msg.required),
    content: yup.string().max(200, msg.max),
    approve: yup.boolean().oneOf([true]).required(),
  })
}

const SeparateForm = ({ data, companyId }: Props) => {
  const mainTitle = extractProperty(data, "mainTitle")
  const text = extractProperty(data, "text")
  const t = useTranslations("TierraContact")
  const reCaptchaRef = useRef<ReCAPTCHA>()
  const locale = useLocale()
  const [checkedMarketing, setCheckedMarketing] = useState<boolean>(false)
  const [checkedCommercial, setCheckedCommercial] = useState<boolean>(false)
  const schema = generateSchema(locale)
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, isValid },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      senderName: "",
      contactUsSubject: "",
      senderEmail: "",
      content: "",
      approve: false,
    },
    resolver: yupResolver(schema),
  })
  const messageValue = watch("content")
  const [sendSucceded, setSendSucceded] = useState<boolean | null>(null)

  const onSubmit = async (data: any) => {
    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string

    const requestData = {
      contactUsSubject: "Lead" as ContactUsSubjects,
      freeTextSubject: data?.contactUsSubject,
      companyId: companyId || -1,
      senderName: data.senderName,
      senderPhone: "",
      senderEmail: data.senderEmail,
      content: data.content,
      contactUsType: "TierraBonita" as "TierraBonita",
      allowedMarketing: checkedMarketing,
      allowedSmsAndEmail: checkedCommercial,
      uri: window?.location?.href,
      gRecaptchaResponse: recaptchaResponse,
    }
    postWebContactUsSendContactUs(requestData)
      .then((res: any) => {
        if (res.data.body) {
          submitSuccess("שליחה", "טופס צור קשר", true)
          setSendSucceded(true)
        } else {
          setSendSucceded(false)
        }
      })
      .catch((error) => {
        submitError("שליחה", "טופס צור קשר ", error?.message)
        setSendSucceded(false)
      })
      .finally(() => {
        reset()
      })
  }
  const handleSendError = () => {
    if (!isValid) {
      if (Object.keys(errors).length > 0) {
        submitError(
          "שליחה",
          "טופס צור קשר ",
          errors?.senderName?.message ||
            errors?.senderEmail?.message ||
            errors?.contactUsSubject?.message,
        )
      } else {
        submitError("שליחה", "טופס צור קשר ", "subject error")
      }
    }
  }

  const { ref: mobileRef, inView: mobileInView } = useInView(useInViewConfig)
  const imageTransitionStyles = useTransitionUpStyles(mobileInView)
  const descriptionTransitionStyles = useTransitionUpStyles(mobileInView, delayTime)

  return (
    <StyledContainer>
      <StyledDiv>
        <StyledFormDiv ref={mobileRef}>
          <NewText
            cssString={styleObjectToCss(imageTransitionStyles)}
            desktopFontSize={48}
            desktopFontWeight="600"
            desktopLineHeight="69px"
            mobileFontSize={40}
            mobileLineHeight={"46px"}
            component="h1"
            color={colors.black}
          >
            {mainTitle}
          </NewText>
          <NewText
            cssString={styleObjectToCss(descriptionTransitionStyles)}
            marginTop="24px"
            marginBottom="24px"
            desktopFontSize={14}
            desktopFontWeight="400"
            desktopLineHeight="20px"
            mobileFontSize={12}
            mobileLineHeight={"17px"}
            component="p"
            color={colors.black}
          >
            {text}
          </NewText>
          {sendSucceded !== null ? (
            <>
              <StyledTextContainer>
                {sendSucceded ? (
                  <FormSubmitSuccessIcon fill={sharedColors.darkGreen} />
                ) : (
                  <FormSubmitFailIcon />
                )}

                <StyledBigContainer>
                  <NewText
                    desktopFontSize={32}
                    desktopLineHeight={"36px"}
                    mobileFontSize={24}
                    mobileFontWeight="400"
                    mobileLineHeight="34.56px"
                    component="h2"
                    color={"black"}
                  >
                    {sendSucceded ? t("contactSucessTitle") : "Your request was not sent"}
                  </NewText>
                  <NewText
                    desktopFontSize={14}
                    desktopFontWeight="400"
                    desktopLineHeight="15.92px"
                    component="p"
                    color={"black"}
                  >
                    {sendSucceded ? (
                      <>
                        <span>Thank you for reaching out to us.</span>
                        <br />
                        <span>Your request has been received.</span>
                      </>
                    ) : (
                      <>
                        <span>Oops!</span>
                        <br />
                        <span>Something went wrong.</span>
                        <br />
                        <span>Please try again later</span>
                      </>
                    )}
                  </NewText>
                </StyledBigContainer>
              </StyledTextContainer>

              {!sendSucceded && (
                <StyledButton onClick={() => setSendSucceded(null)}>
                  {locale === "he" ? " ניסיון נוסף" : "Try Again"}
                </StyledButton>
              )}
            </>
          ) : (
            <>
              <FormContainer onSubmit={handleSubmit(onSubmit)}>
                <TransitionUp $inView={mobileInView} $delay={transitionDelayTime * 3}>
                  <Controller
                    name="senderName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("fullName")}
                        type="text"
                        variant="filled"
                        error={!!errors.senderName}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted}
                            error={!!errors.senderName}
                            text={errors.senderName?.message}
                          />
                        }
                        sx={{
                          marginBlockEnd: "24px",
                        }}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="senderEmail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("email")}
                        type="email"
                        variant="filled"
                        error={!!errors.senderEmail}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted}
                            error={!!errors.senderEmail}
                            text={errors.senderEmail?.message}
                          />
                        }
                        fullWidth
                        sx={{
                          marginBlockEnd: "24px",
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="contactUsSubject"
                    control={control}
                    render={({ field }) => {
                      return (
                        <TextField
                          {...field}
                          label={t("subject")}
                          type="text"
                          variant="filled"
                          error={!!errors.contactUsSubject}
                          helperText={
                            <CustomHelperText
                              render={isSubmitted}
                              error={!!errors.contactUsSubject}
                              text={errors.contactUsSubject?.message}
                            />
                          }
                          fullWidth
                          sx={{
                            marginBlockEnd: "24px",
                          }}
                        />
                      )
                    }}
                  />
                  <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("message")}
                        type="text"
                        variant="filled"
                        error={false}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted}
                            error={false}
                            text={errors.content?.message}
                          />
                        }
                        fullWidth
                        multiline
                        rows={3.6}
                        inputProps={{
                          maxLength: 200,
                          style: {
                            resize: "vertical",
                          },
                        }}
                        sx={{
                          "& .MuiFormHelperText-root": {
                            bottom: "-22px",
                          },
                          "& .MuiInputLabel-root": {
                            top: "-5px",
                          },
                        }}
                      />
                    )}
                  />
                  <InputLengthContainer>
                    <NewText
                      desktopFontSize={14}
                      desktopFontWeight="400"
                      desktopLineHeight="20.16px"
                      component="p"
                      color={"black"}
                    >
                      {messageValue.length}/200
                    </NewText>
                  </InputLengthContainer>
                  <CheckBoxesContainer>
                    <StyledCheckboxContainer>
                      <StyledCheckbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckedCheckboxIcon />}
                        sx={{
                          ":hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        inputProps={{ "aria-label": t("marketingMessage") }}
                        status={true}
                        value={checkedMarketing}
                        onChange={() => setCheckedMarketing((el) => !el)}
                      />

                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight="400"
                          desktopLineHeight="20px"
                          mobileFontSize={12}
                          mobileFontWeight="400"
                          mobileLineHeight={"17px"}
                          component="p"
                          color={"black"}
                          maxWidth={"750px"}
                        >
                          {t("marketingMessage")}
                        </NewText>
                      </div>
                    </StyledCheckboxContainer>
                    <StyledCheckboxContainer>
                      <StyledCheckbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckedCheckboxIcon />}
                        sx={{
                          ":hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        inputProps={{ "aria-label": t("commercialMessage") }}
                        status={true}
                        value={checkedCommercial}
                        onChange={() => setCheckedCommercial((el) => !el)}
                      />

                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight="400"
                          desktopLineHeight="20px"
                          mobileFontSize={12}
                          mobileFontWeight="400"
                          mobileLineHeight={"17px"}
                          component="p"
                          color={"black"}
                          maxWidth={"750px"}
                          dangerouslySetInnerHTML={{
                            __html: t("commercialMessage"),
                          }}
                        ></NewText>
                      </div>
                    </StyledCheckboxContainer>
                  </CheckBoxesContainer>
                  <CheckBoxesContainer>
                    <StyledCheckboxContainer>
                      <Controller
                        name="approve"
                        control={control}
                        render={({ field }) => (
                          <StyledCheckbox
                            {...field}
                            icon={<CheckboxIcon />}
                            checkedIcon={<CheckedCheckboxIcon />}
                            sx={{
                              ":hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                            inputProps={{ "aria-label": `${t("accept")} ${t("terms")}` }}
                            status={field.value}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight="400"
                          desktopLineHeight="20px"
                          mobileFontSize={12}
                          mobileFontWeight="400"
                          mobileLineHeight={"17px"}
                          component="p"
                          color={"black"}
                          maxWidth={"750px"}
                        >
                          {t("accept")}{" "}
                          <a
                            style={{ color: "inherit" }}
                            target="_blank"
                            href="https://testcms.ashtrom.co.il/media/jh4ntygz/%D7%91%D7%93%D7%99%D7%A7%D7%AA-%D7%A7%D7%95%D7%91%D7%A5-pdf.pdf"
                          >
                            {t("terms")}
                          </a>
                        </NewText>
                      </div>
                    </StyledCheckboxContainer>
                  </CheckBoxesContainer>
                  <StyledButtonWrapper>
                    <StyledButton
                      type="submit"
                      onClick={() => {
                        submitClick(" שליחה", "טופס צור קשר")
                        handleSendError()
                      }}
                    >
                      <NewText
                        component={"p"}
                        desktopFontSize={16}
                        desktopFontWeight={600}
                        color={sharedColors.white}
                      >
                        {t("send")}
                      </NewText>
                    </StyledButton>
                  </StyledButtonWrapper>{" "}
                </TransitionUp>
                {RECAPTCHA_KEY && (
                  <ReCAPTCHA
                    ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
                    sitekey={RECAPTCHA_KEY}
                    size="invisible"
                    hl="iw"
                    tabIndex={-1}
                  />
                )}
              </FormContainer>
            </>
          )}
        </StyledFormDiv>
      </StyledDiv>
    </StyledContainer>
  )
}

export default SeparateForm
